package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.model.pages.AddBagRequiredField
import com.example.mystybag.appCore.model.pages.AddBagSectionTab
import com.example.mystybag.appCore.model.pages.AddBagUIState
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.logic.base.MainCallback
import com.example.mystybag.login.logic.NavigatorReturnToDashboard
import com.example.mystybag.sharedmodels.project.requestModels.merchant.CreateBagRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.UpdateBagRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetLocationProductsRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantLocationsRequestModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.BagProductSelection
import com.example.mystybag.sharedmodels.project.responseModels.merchant.BagProductSelectionMode
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetLocationProductsResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.MerchantLocationResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetBagDetailsResponseModel
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

class AddBagViewModel(
    context: AppContext,
    private val merchantLocationId: Long,
    private val businessCategoryId: Long? = null,
    private val bagId: Long? = null
) : ChildViewModel<AddBagUIState>(
    context,
    AddBagUIState(
        merchantLocationId = merchantLocationId,
        businessCategoryId = businessCategoryId,
        bagId = bagId,
        isEditMode = bagId != null
    )
) {
    private val tabOrder = listOf(
        AddBagSectionTab.DETAILS,
        AddBagSectionTab.PRICE,
        AddBagSectionTab.COLLECT_TIME,
        AddBagSectionTab.PRODUCTS
    )

    companion object {
        // Minimum and maximum amount limits
        private const val MIN_AMOUNT = 5.0
        private const val MAX_AMOUNT = 10000.0 // Reasonable max for bag prices
        // Discount percentage limits (step 1%)
        private const val MAX_DISCOUNT_PERCENT = 100
        private const val BAGS_COUNT_MAX = 99
    }

    override fun onCreate() {
        navControls.title = if (bagId != null) sharedString(SR.strings.edit_bag) else sharedString(SR.strings.add_bag)
        navControls.backButtonVisible = true
        if (bagId == null) {
            updateState { s -> if (s.bagsCount.isEmpty()) s.copy(bagsCount = "1") else s }
        }
        loadMerchantLocations()
        loadLocationProducts()
        if (bagId != null) loadBagDetailsForEdit()
    }

    /** Loads the product catalog (categories + products) for the currently selected location. */
    private fun loadLocationProducts() {
        val locationId = uiState.value.merchantLocationId
        if (locationId <= 0L) return
        scope.launch {
            updateState { it.copy(isLoadingProducts = true) }
            commandBlock(showProgress = false) {
                CommonCommands.getLocationProducts(GetLocationProductsRequestModel(merchantLocationId = locationId))
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val data = response as? GetLocationProductsResponseModel
                                updateState {
                                    it.copy(
                                        availableProductCategories = data?.categories ?: emptyList(),
                                        availableProducts = data?.products ?: emptyList(),
                                        isLoadingProducts = false
                                    )
                                }
                            }
                        },
                        baseError = {
                            launchOnMain { updateState { it.copy(isLoadingProducts = false) } }
                        }
                    )
            }
        }
    }

    /** Sets how the bag's products are chosen (all / by category / custom). */
    fun setProductSelectionMode(mode: BagProductSelectionMode) {
        updateState { it.copy(productSelectionMode = mode) }
    }

    fun setContainsProductsForPersonsOver18(checked: Boolean) {
        updateState { it.copy(containsProductsForPersonsOver18 = checked) }
    }

    fun updateProductSearchQuery(value: String) {
        updateState { it.copy(productSearchQuery = value) }
    }

    /** Toggles a category whose products will be included (mode = CATEGORY, supports multiple). */
    fun toggleProductCategory(categoryId: Long) {
        updateState { state ->
            val newSelected = if (state.selectedProductCategoryIds.contains(categoryId)) {
                state.selectedProductCategoryIds - categoryId
            } else {
                state.selectedProductCategoryIds + categoryId
            }
            state.copy(
                selectedProductCategoryIds = newSelected,
                productSelectionMode = BagProductSelectionMode.CATEGORY
            )
        }
    }

    /** Toggles a single product in the custom selection (mode = CUSTOM). */
    fun toggleProduct(productId: Long) {
        updateState { state ->
            val newSelected = if (state.selectedProductIds.contains(productId)) {
                state.selectedProductIds - productId
            } else {
                state.selectedProductIds + productId
            }
            state.copy(
                selectedProductIds = newSelected,
                productSelectionMode = BagProductSelectionMode.CUSTOM
            )
        }
    }

    /** Builds the product selection to send to the server based on the current UI state. */
    private fun buildProductSelection(state: AddBagUIState): BagProductSelection = when (state.productSelectionMode) {
        BagProductSelectionMode.ALL -> BagProductSelection(mode = BagProductSelectionMode.ALL)
        BagProductSelectionMode.CATEGORY -> {
            val categoryIds = state.selectedProductCategoryIds.toList()
            BagProductSelection(
                mode = BagProductSelectionMode.CATEGORY,
                // Backward compatibility: keep single field populated when only one is chosen.
                categoryId = categoryIds.singleOrNull(),
                categoryIds = categoryIds
            )
        }
        BagProductSelectionMode.CUSTOM -> BagProductSelection(
            mode = BagProductSelectionMode.CUSTOM,
            productIds = state.selectedProductIds.toList()
        )
    }

    /** Loads bag details when in edit mode and fills the form. */
    private fun loadBagDetailsForEdit() {
        val id = this.bagId ?: return
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.getBagDetails(id)
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val details = response as? GetBagDetailsResponseModel ?: return@launchOnMain
                                val state = uiState.value
                                var activeFrom = details.activeFrom?.let { parseLocalDate(it) }
                                var activeTo = details.activeTo?.let { parseLocalDate(it) }

                                val discountPercent = details.priceNormal?.let { pn ->
                                    details.priceWithDiscount?.let { pd ->
                                        if (pn > 0) ((1 - pd / pn) * 100).toInt().coerceIn(30, 100) else 30
                                    }
                                } ?: 30
                                val priceNormalStr = details.priceNormal?.let { p -> com.example.mystybag.core.expected.format("%.2f", p) } ?: ""
                                val priceWithDiscountStr = details.priceWithDiscount?.let { p -> com.example.mystybag.core.expected.format("%.2f", p) }
                                updateState {
                                    it.copy(
                                        merchantLocationId = details.locationId ?: it.merchantLocationId,
                                        title = details.title.orEmpty(),
                                        isEnabled = details.isEnabled ?: it.isEnabled,
                                        bagsCount = (details.totalCount ?: 0).toString(),
                                        priceNormal = priceNormalStr,
                                        discountPercentageInput = discountPercent.toString(),
                                        priceWithDiscountLoaded = priceWithDiscountStr,
                                        currency = details.currency ?: it.currency,
                                        activeFrom = activeFrom,
                                        activeTo = activeTo,
                                        pickupTimeFrom = details.pickupTimeFrom.orEmpty(),
                                        pickupTimeTo = details.pickupTimeTo.orEmpty(),
                                        productSelectionMode = details.productSelectionMode,
                                        selectedProductCategoryIds = details.productCategoryIds?.toSet()
                                            ?: listOfNotNull(details.productCategoryId).toSet(),
                                        selectedProductIds = details.products.map { p -> p.id }.toSet(),
                                        containsProductsForPersonsOver18 = details.containsProductsForPersonsOver18
                                    )
                                }
                                loadLocationProducts()
                            }
                        },
                        baseError = { }
                    )
            }
        }
    }

    private fun parseLocalDate(s: String): kotlinx.datetime.LocalDate? = try {
        kotlinx.datetime.LocalDate.parse(s.take(10))
    } catch (_: Exception) {
        null
    }

    /** Loads all merchant locations for the location dropdown and sets availableLocations. */
    private fun loadMerchantLocations() {
        scope.launch {
            commandBlock(showProgress = false) {
                val merchantId = com.example.mystybag.core.expected.Preferences.getLong(
                    key = com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID,
                    0L
                )
                if (merchantId == 0L) return@commandBlock
                CommonCommands.getMerchantLocations(GetMerchantLocationsRequestModel(merchantId = merchantId))
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val locations = (response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantLocationsResponseModel)?.locations ?: emptyList()
                                val singleLocationId = if (locations.size == 1) locations.single().id else null
                                updateState { state ->
                                    state.copy(
                                        availableLocations = locations,
                                        merchantLocationId = singleLocationId ?: state.merchantLocationId
                                    )
                                }
                                singleLocationId?.let {
                                    loadLocationProducts()
                                }
                            }
                        },
                        baseError = { }
                    )
            }
        }
    }

    /** Called when user selects a location in the dropdown. Updates selected location and reloads the catalog. */
    fun selectLocation(location: MerchantLocationResponseModel) {
        updateState {
            it.copy(
                merchantLocationId = location.id,
                requiredFieldErrors = it.requiredFieldErrors - AddBagRequiredField.LOCATION,
                // Reset product selection because the catalog is per-location.
                productSelectionMode = BagProductSelectionMode.ALL,
                selectedProductCategoryIds = emptySet(),
                selectedProductIds = emptySet()
            )
        }
        loadLocationProducts()
    }

    fun updateBagsCount(value: String) {
        // Filter to only allow digits (0-9)
        val filteredValue = value.filter { it.isDigit() }
        updateState {
            it.copy(
                bagsCount = filteredValue,
                requiredFieldErrors = it.requiredFieldErrors - AddBagRequiredField.BAGS_COUNT
            )
        }
    }

    fun decrementBagsCount() {
        val current = uiState.value.bagsCount.toIntOrNull() ?: 1
        val next = (current - 1).coerceAtLeast(1)
        updateBagsCount(next.toString())
    }

    fun incrementBagsCount() {
        val raw = uiState.value.bagsCount.toIntOrNull()
        val base = when {
            raw == null || raw < 1 -> 0
            else -> raw
        }
        val next = (base + 1).coerceIn(1, BAGS_COUNT_MAX)
        updateBagsCount(next.toString())
    }

    fun updatePriceNormal(value: String) {
        val filteredValue = filterDecimalInput(value)
        val errorMessage = validateAmount(filteredValue)
        updateState {
            it.copy(
                priceNormal = filteredValue,
                priceNormalError = errorMessage,
                priceWithDiscountLoaded = if (it.isEditMode) null else it.priceWithDiscountLoaded,
                requiredFieldErrors = it.requiredFieldErrors - AddBagRequiredField.PRICE_NORMAL
            )
        }
    }

    /** Called when the price field gains focus; View uses this to move cursor to end. */
    fun onPriceNormalFocusGained() {
        updateState { it.copy(movePriceCursorToEndTrigger = it.movePriceCursorToEndTrigger + 1) }
    }

    /**
     * Called when the price field loses focus. If the value has no decimal point or does not have
     * exactly two digits after it, formats as "X.00" (e.g. "34" -> "34.00", "34.1" -> "34.10").
     */
    fun onPriceNormalFocusLost() {
        val state = uiState.value
        val current = state.priceNormal
        if (current.isBlank()) return
        val parsed = parsePriceToDouble(current) ?: return
        val hasDecimal = current.contains('.')
        val decimals = if (hasDecimal) current.substringAfter('.').take(2) else ""
        val needsFormat = !hasDecimal || decimals.length != 2
        if (!needsFormat) return
        val formatted = com.example.mystybag.core.expected.format("%.2f", parsed)
        updateState {
            it.copy(
                priceNormal = formatted,
                priceNormalError = validateAmount(formatted),
                priceWithDiscountLoaded = if (it.isEditMode) null else it.priceWithDiscountLoaded
            )
        }
    }

    /** Called by the View after it has moved the cursor to end (consumes the trigger). */
    fun ackPriceNormalCursorMoved() {
        updateState { it.copy(movePriceCursorToEndTrigger = 0) }
    }

    fun updateDiscountPercentage(value: String) {
        val digitsOnly = value.filter { it.isDigit() }.take(3)
        val parsed = digitsOnly.toIntOrNull()
        val displayValue = when {
            digitsOnly.isEmpty() -> ""
            parsed != null -> parsed.coerceAtMost(MAX_DISCOUNT_PERCENT).toString()
            else -> digitsOnly
        }

        updateState {
            it.copy(
                discountPercentageInput = displayValue,
                priceWithDiscountLoaded = if (it.isEditMode) null else it.priceWithDiscountLoaded,
                requiredFieldErrors = it.requiredFieldErrors - AddBagRequiredField.DISCOUNT_PERCENTAGE
            )
        }
    }

    fun setDiscountPercentage(percent: Int) {
        val clamped = percent.coerceIn(0, MAX_DISCOUNT_PERCENT)
        updateState {
            it.copy(
                discountPercentageInput = clamped.toString(),
                discountPercentageError = "",
                priceWithDiscountLoaded = if (it.isEditMode) null else it.priceWithDiscountLoaded,
                requiredFieldErrors = it.requiredFieldErrors - AddBagRequiredField.DISCOUNT_PERCENTAGE
            )
        }
    }

    /** Parsed discount percentage (30–100), or null if invalid/empty. */
    fun getDiscountPercentageInt(state: AddBagUIState): Int? {
        val parsed = state.discountPercentageInput.toIntOrNull() ?: return null
        return parsed.takeIf { it in 0..MAX_DISCOUNT_PERCENT }
    }

    /**
     * Parses price string to Double according to Euro amount standards:
     * - Accepts comma or dot as decimal separator (EU locale vs. programmatic)
     * - Strips spaces and non-breaking space (EU thousands separator)
     */
    private fun parsePriceToDouble(s: String): Double? =
        s.replace(" ", "").replace("\u00A0", "").replace(',', '.').trim().toDoubleOrNull()

    /**
     * Computed price with discount from normal price and discount percentage.
     * Formula: priceNormal * (1 - discountPercentage/100), rounded to 2 decimals.
     */
    fun getComputedPriceWithDiscount(priceNormal: String, discountPercentage: Int): Double? {
        val normal = parsePriceToDouble(priceNormal) ?: return null
        if (normal <= 0.0) return null
        val multiplier = 1.0 - (discountPercentage / 100.0)
        val value = normal * multiplier
        return kotlin.math.round(value * 100) / 100.0
    }
    
    /**
     * Validates amount and returns human-readable error message if invalid.
     * Uses parsePriceToDouble so comma, dot, and spaces are handled consistently.
     */
    private fun validateAmount(amount: String): String {
        if (amount.isEmpty()) return ""
        val amountValue = parsePriceToDouble(amount) ?: return ""
        return when {
            amountValue < MIN_AMOUNT -> {
                val formattedMin = com.example.mystybag.core.expected.format("%.2f", MIN_AMOUNT)
                sharedString(SR.strings.amount_min_error, formattedMin)
            }
            amountValue > MAX_AMOUNT -> {
                val formattedMax = com.example.mystybag.core.expected.format("%.2f", MAX_AMOUNT)
                sharedString(SR.strings.amount_max_error, formattedMax)
            }
            else -> ""
        }
    }
    
    /**
     * Filters input for Euro amount (EUR) according to standard rules:
     * - Decimal separator: comma or dot (normalized to dot for storage)
     * - Thousands separator: space or non-breaking space (stripped)
     * - At most 2 decimal places (EUR cents)
     * - No forced padding while typing (user can enter "20", "20.5", "20.50")
     * - Leading zeros in integer part removed (012.50 → 12.50)
     */
    private fun filterDecimalInput(input: String): String {
        if (input.isEmpty()) return ""
        val noSpaces = input.replace(" ", "").replace("\u00A0", "")
        val normalized = noSpaces.replace(',', '.')
        val filtered = normalized.filter { it.isDigit() || it == '.' }
        val parts = filtered.split('.')
        return when {
            parts.size == 1 -> {
                val integerPart = parts[0]
                if (integerPart.isEmpty()) ""
                else integerPart.trimStart('0').ifEmpty { "0" }
            }
            else -> {
                val integerPart = parts[0]
                val decimalPart = parts[1].take(2)
                when {
                    integerPart.isEmpty() && decimalPart.isEmpty() -> ""
                    integerPart.isEmpty() -> "0.$decimalPart"
                    else -> {
                        val cleanedInteger = integerPart.trimStart('0').ifEmpty { "0" }
                        "$cleanedInteger.$decimalPart"
                    }
                }
            }
        }
    }

    fun updateCurrency(value: String) {
        updateState { it.copy(currency = value) }
    }

    fun updateActiveFrom(value: LocalDate?) {
        updateState { state ->
            // If activeFrom is set and activeTo exists but is before activeFrom, clear activeTo
            val correctedActiveTo = if (value != null && state.activeTo != null && state.activeTo < value) {
                null
            } else {
                state.activeTo
            }
            // Same date is allowed (one-day offer). Error only when activeTo < activeFrom.
            val activeToError = if (value != null && correctedActiveTo != null && correctedActiveTo < value) {
                sharedString(SR.strings.active_to_must_be_after_active_from)
            } else {
                "" // Clear error if dates are valid
            }
            state.copy(
                activeFrom = value,
                activeTo = correctedActiveTo,
                activeToError = activeToError,
                requiredFieldErrors = state.requiredFieldErrors - AddBagRequiredField.ACTIVE_FROM
            )
        }
    }

    fun updateActiveTo(value: LocalDate?) {
        updateState { state ->
            val activeToError = if (value != null && state.activeFrom != null && value < state.activeFrom) {
                // Show error if activeTo is before activeFrom
                sharedString(SR.strings.active_to_must_be_after_active_from)
            } else {
                ""
            }
            state.copy(
                activeTo = value,
                activeToError = activeToError,
                requiredFieldErrors = state.requiredFieldErrors - AddBagRequiredField.ACTIVE_TO
            )
        }
    }

    fun updatePickupTimeFrom(value: String) {
        updateState {
            it.copy(
                pickupTimeFrom = value.trim(),
                requiredFieldErrors = it.requiredFieldErrors - AddBagRequiredField.PICKUP_TIME_FROM
            )
        }
    }

    fun updatePickupTimeTo(value: String) {
        updateState {
            it.copy(
                pickupTimeTo = value.trim(),
                requiredFieldErrors = it.requiredFieldErrors - AddBagRequiredField.PICKUP_TIME_TO
            )
        }
    }

    fun updateTitle(value: String) {
        updateState {
            it.copy(
                title = value,
                requiredFieldErrors = it.requiredFieldErrors - AddBagRequiredField.TITLE
            )
        }
    }

    private fun parseTimeToMinutes(value: String): Int? {
        val parts = value.trim().split(":")
        if (parts.size != 2) return null
        val h = parts[0].toIntOrNull() ?: return null
        val m = parts[1].toIntOrNull() ?: return null
        if (h !in 0..23 || m !in 0..59) return null
        return h * 60 + m
    }

    private fun isPickupTimeRangeValid(from: String, to: String): Boolean {
        val fromMinutes = parseTimeToMinutes(from) ?: return true
        val toMinutes = parseTimeToMinutes(to) ?: return true
        return fromMinutes < toMinutes
    }

    fun saveBag(currentTab: AddBagSectionTab) {
        if (uiState.value.isEditMode) updateBag(currentTab) else createBag(currentTab)
    }

    private fun updateBag(currentTab: AddBagSectionTab) {
        val state = uiState.value
        if (!validateRequiredFields(currentTab, state)) return
        val id = state.bagId ?: return
        val discountPercent = getDiscountPercentageInt(state)
        if (discountPercent == null) return
        if (state.activeTo!! < state.activeFrom!!) {
            showSnackbar(sharedString(SR.strings.active_to_must_be_after_active_from))
            return
        }
        if (state.pickupTimeFrom.isNotEmpty() && state.pickupTimeTo.isNotEmpty() && !isPickupTimeRangeValid(state.pickupTimeFrom, state.pickupTimeTo)) {
            showSnackbar(sharedString(SR.strings.invalid_pickup_time_range))
            return
        }
        val bagsCount = state.bagsCount.toIntOrNull()
        if (bagsCount == null || bagsCount < 1) {
            showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
            return
        }
        val priceNormal = parsePriceToDouble(state.priceNormal)
        if (priceNormal == null || priceNormal <= 0) {
            showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
            return
        }
        val priceWithDiscount = getComputedPriceWithDiscount(state.priceNormal, discountPercent)
        if (priceWithDiscount == null) {
            showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
            return
        }
        val pickupFrom = state.pickupTimeFrom.trim().takeIf { it.isNotEmpty() }
        val pickupTo = state.pickupTimeTo.trim().takeIf { it.isNotEmpty() }
        val activeFromDateTime = LocalDateTime(
            year = state.activeFrom!!.year,
            monthNumber = state.activeFrom!!.monthNumber,
            dayOfMonth = state.activeFrom!!.dayOfMonth,
            hour = 0,
            minute = 0
        )
        val sameDay = state.activeFrom == state.activeTo
        val activeToDateTime = LocalDateTime(
            year = state.activeTo!!.year,
            monthNumber = state.activeTo!!.monthNumber,
            dayOfMonth = state.activeTo!!.dayOfMonth,
            hour = if (sameDay) 23 else 0,
            minute = if (sameDay) 59 else 0
        )
        scope.launch {
            updateState { it.copy(isLoading = true) }
            commandBlock(showProgress = true) {
                CommonCommands.updateBag(
                    UpdateBagRequestModel(
                        bagId = id,
                        merchantLocationId = state.merchantLocationId,
                        title = state.title.trim().takeIf { it.isNotEmpty() },
                        activeFrom = activeFromDateTime,
                        activeTo = activeToDateTime,
                        pickupTimeFrom = pickupFrom,
                        pickupTimeTo = pickupTo,
                        bagsCount = bagsCount,
                        priceNormal = priceNormal,
                        priceWithDiscount = priceWithDiscount,
                        isEnabled = state.isEnabled,
                        currency = state.currency,
                        productSelection = buildProductSelection(state),
                        containsProductsForPersonsOver18 = state.containsProductsForPersonsOver18
                    )
                )
                    .onResponse(
                        success = {
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(sharedString(SR.strings.save))
                                goBack()
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage))
                            }
                        }
                    )
            }
        }
    }

    fun showPriceMaxExceededMessage() {
        showSnackbar(sharedString(SR.strings.filter_price_max_999))
    }

    fun createBag(currentTab: AddBagSectionTab) {
        val state = uiState.value
        if (!validateRequiredFields(currentTab, state)) return

        val discountPercent = getDiscountPercentageInt(state)
        if (discountPercent == null) {
            showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
            return
        }
        val activeFrom = state.activeFrom ?: return
        val activeTo = state.activeTo ?: return

        // Validate activeTo >= activeFrom (same date is valid for one-day offers)
        if (activeTo < activeFrom) {
            showSnackbar(sharedString(SR.strings.active_to_must_be_after_active_from))
            return
        }
        if (state.pickupTimeFrom.isNotEmpty() && state.pickupTimeTo.isNotEmpty() && !isPickupTimeRangeValid(state.pickupTimeFrom, state.pickupTimeTo)) {
            showSnackbar(sharedString(SR.strings.invalid_pickup_time_range))
            return
        }

        val bagsCount = state.bagsCount.toIntOrNull() ?: return
        val priceNormal = parsePriceToDouble(state.priceNormal) ?: return
        val priceWithDiscount = getComputedPriceWithDiscount(state.priceNormal, discountPercent) ?: return

        scope.launch {
            updateState { it.copy(isLoading = true) }

            commandBlock(showProgress = true) {
                // Convert LocalDate to LocalDateTime. Use 00:00 for activeFrom.
                val activeFromDateTime = LocalDateTime(
                    year = activeFrom.year,
                    monthNumber = activeFrom.monthNumber,
                    dayOfMonth = activeFrom.dayOfMonth,
                    hour = 0,
                    minute = 0
                )
                // When same date, use end-of-day (23:59) for activeTo so the bag is active the whole day and backend accepts the range
                val sameDay = activeFrom == activeTo
                val activeToDateTime = LocalDateTime(
                    year = activeTo.year,
                    monthNumber = activeTo.monthNumber,
                    dayOfMonth = activeTo.dayOfMonth,
                    hour = if (sameDay) 23 else 0,
                    minute = if (sameDay) 59 else 0
                )
                val pickupFrom = state.pickupTimeFrom.trim().takeIf { it.isNotEmpty() }
                val pickupTo = state.pickupTimeTo.trim().takeIf { it.isNotEmpty() }
                CommonCommands.createBag(
                    CreateBagRequestModel(
                        merchantLocationId = state.merchantLocationId,
                        title = state.title.trim().takeIf { it.isNotEmpty() },
                        activeFrom = activeFromDateTime,
                        activeTo = activeToDateTime,
                        pickupTimeFrom = pickupFrom,
                        pickupTimeTo = pickupTo,
                        bagsCount = bagsCount,
                        priceNormal = priceNormal,
                        priceWithDiscount = priceWithDiscount,
                        currency = state.currency,
                        productSelection = buildProductSelection(state),
                        containsProductsForPersonsOver18 = state.containsProductsForPersonsOver18
                    )
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                // Navigate back to Dashboard and remove AddFirstBagPromptView from stack
                                // Use NavigatorReturnToDashboard to properly clean up the stack
                                setResult<MainCallback> {
                                    popToMainAndNavigate(NavigatorReturnToDashboard)
                                }
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage))
                            }
                        }
                    )
            }
        }
    }

    private fun validateRequiredFields(currentTab: AddBagSectionTab, state: AddBagUIState): Boolean {
        val missingFields = collectMissingRequiredFields(state)
        if (missingFields.isEmpty()) {
            updateState {
                it.copy(
                    requiredFieldErrors = emptySet(),
                    validationTargetTab = null,
                    validationTargetField = null
                )
            }
            return true
        }

        val missingTabs = missingFields.map(::fieldTab).toSet()
        val targetTab = when {
            missingFields.size == 1 -> fieldTab(missingFields.first())
            missingTabs.size == 1 -> missingTabs.first()
            currentTab !in missingTabs -> nextTabWithMissing(currentTab, missingTabs)
            else -> currentTab
        }
        val targetField = missingFields.singleOrNull()

        updateState {
            it.copy(
                requiredFieldErrors = missingFields.toSet(),
                validationTargetTab = targetTab,
                validationTargetField = targetField,
                validationRequestId = it.validationRequestId + 1
            )
        }
        showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
        return false
    }

    private fun collectMissingRequiredFields(state: AddBagUIState): List<AddBagRequiredField> {
        val missing = mutableListOf<AddBagRequiredField>()
        if (state.merchantLocationId <= 0L) missing += AddBagRequiredField.LOCATION
        if (state.title.trim().isEmpty()) missing += AddBagRequiredField.TITLE
        if (state.activeFrom == null) missing += AddBagRequiredField.ACTIVE_FROM
        if (state.activeTo == null) missing += AddBagRequiredField.ACTIVE_TO
        if (state.pickupTimeFrom.trim().isEmpty()) missing += AddBagRequiredField.PICKUP_TIME_FROM
        if (state.pickupTimeTo.trim().isEmpty()) missing += AddBagRequiredField.PICKUP_TIME_TO

        val bagsCount = state.bagsCount.toIntOrNull()
        if (bagsCount == null || bagsCount < 1) missing += AddBagRequiredField.BAGS_COUNT

        val priceNormal = parsePriceToDouble(state.priceNormal)
        if (priceNormal == null || priceNormal <= 0.0) missing += AddBagRequiredField.PRICE_NORMAL

        if (getDiscountPercentageInt(state) == null) missing += AddBagRequiredField.DISCOUNT_PERCENTAGE
        return missing
    }

    private fun fieldTab(field: AddBagRequiredField): AddBagSectionTab = when (field) {
        AddBagRequiredField.LOCATION,
        AddBagRequiredField.TITLE,
        AddBagRequiredField.ACTIVE_FROM,
        AddBagRequiredField.ACTIVE_TO,
        AddBagRequiredField.BAGS_COUNT -> AddBagSectionTab.DETAILS

        AddBagRequiredField.PICKUP_TIME_FROM,
        AddBagRequiredField.PICKUP_TIME_TO -> AddBagSectionTab.COLLECT_TIME

        AddBagRequiredField.PRICE_NORMAL,
        AddBagRequiredField.DISCOUNT_PERCENTAGE -> AddBagSectionTab.PRICE
    }

    private fun nextTabWithMissing(
        currentTab: AddBagSectionTab,
        missingTabs: Set<AddBagSectionTab>
    ): AddBagSectionTab {
        val currentIndex = tabOrder.indexOf(currentTab).coerceAtLeast(0)
        repeat(tabOrder.size) { step ->
            val candidate = tabOrder[(currentIndex + step + 1) % tabOrder.size]
            if (candidate in missingTabs) return candidate
        }
        return missingTabs.firstOrNull() ?: currentTab
    }
}

